import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as FaIcons from "react-icons/fa";
import { Modal } from "react-bootstrap";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";
import { countries_code } from "../../utilis/constants";
import Select from "react-select";
import "./agentBonus.css";

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i}>
        <span
          className={`cms-agent-bonus-skeleton-bone${
            i === 0 || i === 7
              ? " cms-agent-bonus-skeleton-bone--badge"
              : i === 8
                ? " cms-agent-bonus-skeleton-bone--short"
                : ""
          }`}
        />
      </td>
    ))}
  </tr>
);

const AgentBonus = () => {
  const history = useNavigate();
  const { providerid } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBonusId, setCurrentBonusId] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const [bonusList, setBonusList] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [allIntakes, setAllIntakes] = useState([]);
  const [formData, setFormData] = useState({
    type: "bonus",
    course_id: [],
    intake_id: [],
    nationality: [],
    min_applications: 1,
    bonus_amount: "",
    currency: "PKR",
    valid_from: "",
    valid_to: "",
    description: "",
    is_active: true,
  });

  const permissions = getPermissions() || {};

  // Fetch courses
  const fetchProviderCourses = async () => {
    try {
      const res = await apiUrl.get(`getProviderAllCourses/${providerid}`);
      if (res.data?.status) {
        const courseList = res.data.data || [];
        setCourses(courseList);

        const intakeMap = new Map();
        courseList?.forEach((course) => {
          if (Array.isArray(course.intakes)) {
            course.intakes.forEach((intake) => {
              if (intake.intake_id && !intakeMap.has(intake.intake_id)) {
                intakeMap.set(intake.intake_id, {
                  intake_id: intake.intake_id,
                  intake_title:
                    intake.intake_title || `Intake ${intake.intake_id}`,
                  course_name: course.name || "Unnamed Course",
                });
              }
            });
          }
        });
        setAllIntakes(Array.from(intakeMap.values()));
      }
    } catch (error) {
      console.error("Error fetching courses/intakes:", error);
      Swal.fire("Error", "Failed to load courses & intakes", "error");
    }
  };

  // Fetch all bonuses/offers/scholarships
  const fetchBonusList = async () => {
    try {
      const res = await apiUrl.get(`provider_bonuses_offers/${providerid}`);
      if (res.data?.status) {
        setBonusList(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching bonuses:", error);
      Swal.fire("Error", "Failed to load bonuses/offers", "error");
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      await Promise.all([fetchProviderCourses(), fetchBonusList()]);
      setIsInitialLoading(false);
    };

    if (providerid) {
      loadInitialData();
    }
  }, [providerid]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const parseMultiValue = (value) => {
    // Normalise backend value into an array of strings
    if (Array.isArray(value)) return value.filter((v) => v !== null && v !== undefined).map(String);
    if (value === null || value === undefined) return [];
    const str = String(value).trim();
    if (!str) return [];
    return str
      .split(",")
      .map((v) => String(v).trim())
      .filter(Boolean);
  };

  const asArrayOrEmpty = (val) =>
    Array.isArray(val) ? val : val ? [val] : [];

  const firstOrNull = (val) => {
    if (Array.isArray(val)) return val.length ? val[0] : null;
    return val ?? null;
  };

  const toggleMinApps = () => {
    setFormData((prev) => ({
      ...prev,
      min_applications: prev.min_applications === 0 ? 1 : 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasCourse = Array.isArray(formData.course_id) && formData.course_id.length > 0;
    const hasIntake = Array.isArray(formData.intake_id) && formData.intake_id.length > 0;
    const hasNationality =
      Array.isArray(formData.nationality) && formData.nationality.length > 0;

    if (!hasCourse && !hasIntake && !hasNationality) {
      Swal.fire(
        "Warning",
        "Please select at least one of: Course, Intake, or Nationality.",
        "warning",
      );
      return;
    }
    if (!formData.bonus_amount || parseFloat(formData.bonus_amount) <= 0) {
      Swal.fire("Warning", "Please enter a valid amount", "warning");
      return;
    }
    if (!formData.valid_from || !formData.valid_to) {
      Swal.fire("Warning", "Please select valid date range", "warning");
      return;
    }
    if (new Date(formData.valid_from) > new Date(formData.valid_to)) {
      Swal.fire("Warning", "Valid From cannot be after Valid To", "warning");
      return;
    }

    setLoading(true);
    try {
      let payload;

      if (isEditMode && currentBonusId) {
        // EDIT MODE: send single values (not arrays) for these three fields
        payload = {
          provider_id: providerid,
          course_id: firstOrNull(formData.course_id),
          intake_id: firstOrNull(formData.intake_id),
          nationality: firstOrNull(formData.nationality),
          min_applications: formData.min_applications,
          bonus_amount: formData.bonus_amount,
          currency: formData.currency,
          valid_from: formData.valid_from,
          valid_to: formData.valid_to,
          is_active: formData.is_active,
          description: formData.description || null,
          type: formData.type,
        };
      } else {
        // CREATE MODE: backend expects arrays
        payload = {
          provider_id: providerid,
          course_id: asArrayOrEmpty(formData.course_id),
          intake_id: asArrayOrEmpty(formData.intake_id),
          nationality: asArrayOrEmpty(formData.nationality),
          min_applications: formData.min_applications,
          bonus_amount: formData.bonus_amount,
          currency: formData.currency,
          valid_from: formData.valid_from,
          valid_to: formData.valid_to,
          is_active: formData.is_active,
          description: formData.description || null,
          type: formData.type,
        };
      }

      let res;
      if (isEditMode && currentBonusId) {
        res = await apiUrl.put(
          `provider_bonuses_offers/${currentBonusId}`,
          payload,
        );
      } else {
        res = await apiUrl.post("provider_bonuses_offers", payload);
      }

      if (res?.data?.status) {
        Swal.fire("Success", res.data.message, "success");
        setShowModal(false);
        resetForm();
        fetchBonusList();
      } else {
        throw new Error(res.data?.message || "Unknown error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: "bonus",
      course_id: [],
      intake_id: [],
      nationality: [],
      min_applications: 1,
      bonus_amount: "",
      currency: "PKR",
      valid_from: "",
      valid_to: "",
      description: "",
      is_active: true,
    });
    setIsEditMode(false);
    setCurrentBonusId(null);
  };

  const handleEdit = (bonus) => {
    setFormData({
      type: bonus.type || "bonus",
      course_id: parseMultiValue(bonus.course_id),
      intake_id: parseMultiValue(bonus.intake_id),
      nationality: parseMultiValue(bonus.nationality),
      min_applications: bonus.min_applications || 1,
      bonus_amount: bonus.bonus_amount || "",
      currency: bonus.currency || "PKR",
      valid_from: bonus.valid_from?.split("T")[0] || "",
      valid_to: bonus.valid_to?.split("T")[0] || "",
      description: bonus.description || "",
      is_active: bonus.is_active,
    });
    setCurrentBonusId(bonus.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This record will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await apiUrl.delete(`provider_bonuses_offers/${id}`);
          if (res?.data?.status) {
            Swal.fire("Deleted!", res.data.message, "success");
            fetchBonusList();
          } else {
            throw new Error("Delete failed");
          }
        } catch (err) {
          Swal.fire("Error", err.message || "Failed to delete", "error");
        }
      }
    });
  };

  const getCourseName = (id) => {
    const course = courses.find((c) => c.id == id);
    return course?.name || "—";
  };

  const getIntakeTitle = (intakeId) => {
    if (!intakeId) return "All Intakes";
    const intake = allIntakes.find((i) => i.intake_id == intakeId);
    return intake ? intake.intake_title : `ID: ${intakeId}`;
  };

  const renderBadgeList = (items) => {
    if (!Array.isArray(items) || items.length === 0) return "All";
    return (
      <div className="cms-agent-bonus-chip-wrap">
        {items.map((label, idx) => (
          <span
            key={`${label}-${idx}`}
            className="cms-agent-bonus-chip"
            title={label}
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  const courseOptions = (courses || []).map((c) => ({
    value: String(c?.id),
    label: c?.name ?? "Unnamed Course",
  }));

  const intakeOptions = (allIntakes || []).map((i) => ({
    value: String(i?.intake_id),
    label: `${i?.intake_title ?? `Intake ${i?.intake_id}`}${i?.course_name ? ` — ${i.course_name}` : ""
      }`,
  }));

  const nationalityOptions = (Array.isArray(countries_code) ? countries_code : []).map(
    (c) => ({
      value: String(c?.IsoCode),
      label: `${c?.value ?? "Unknown"} (${c?.IsoCode ?? ""})`,
    }),
  );

  const selectedCourseValue = isEditMode
    ? courseOptions.find((o) => (formData.course_id || [])[0] === o.value) || null
    : courseOptions.filter((o) => (formData.course_id || []).includes(o.value));

  const selectedIntakeValue = isEditMode
    ? intakeOptions.find((o) => (formData.intake_id || [])[0] === o.value) || null
    : intakeOptions.filter((o) => (formData.intake_id || []).includes(o.value));

  const selectedNationalityValue = isEditMode
    ? nationalityOptions.find(
      (o) => (formData.nationality || [])[0] === o.value,
    ) || null
    : nationalityOptions.filter((o) =>
      (formData.nationality || []).includes(o.value),
    );

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderColor: state.isFocused ? "#86b7fe" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(13,110,253,.15)" : "none",
      borderRadius: 8,
      fontSize: 12,
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 999,
      background: "#f1f5f9",
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };

  // Filter list by tab
  const filteredList =
    activeTab === "all"
      ? bonusList
      : bonusList.filter((item) => item.type === activeTab);

  // Get count by type
  const getCount = (type) => bonusList.filter((b) => b.type === type).length;
  const getCountryName = (isoCode) => {
    return countries_code.find((c) => c.IsoCode === isoCode)?.value || "—";
  };

  const tabs = [
    { key: "all", label: "All", count: bonusList.length },
    { key: "bonus", label: "Bonuses", count: getCount("bonus") },
    { key: "offer", label: "Offers", count: getCount("offer") },
    { key: "scholarship", label: "Scholarships", count: getCount("scholarship") },
  ];

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    if (loading) return;
    setShowModal(false);
  };

  const typeBadgeClass = (type) => {
    if (type === "offer") return "cms-agent-bonus-type-badge--offer";
    if (type === "scholarship") return "cms-agent-bonus-type-badge--scholarship";
    return "cms-agent-bonus-type-badge--bonus";
  };

  const emptyLabel =
    activeTab === "all" ? "incentives" : `${activeTab}s`;

  return (
    <div className="cms-agent-bonus-page">
      <button
        type="button"
        className="cms-agent-bonus-back"
        onClick={() => history(-1)}
      >
        <FaIcons.FaChevronLeft size={10} />
        Back to Provider
      </button>

      <div className="cms-agent-bonus-header">
        <div className="cms-agent-bonus-header-text">
          <h1 className="cms-agent-bonus-title">
            Bonuses, Offers & Scholarships
          </h1>
          <p className="cms-agent-bonus-subtitle">
            Manage all incentive types for this provider
          </p>
        </div>
        {permissions.can_edit_provider && (
          <button
            type="button"
            className="cms-agent-bonus-add-btn"
            onClick={openAddModal}
          >
            <FaIcons.FaPlus size={11} />
            Add Incentive
          </button>
        )}
      </div>

      <div className="cms-agent-bonus-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`cms-agent-bonus-tab${
              activeTab === tab.key ? " is-active" : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="cms-agent-bonus-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="cms-agent-bonus-table-card">
        <div className="cms-agent-bonus-table-toolbar">
          <p className="cms-agent-bonus-table-toolbar-text">
            {isInitialLoading
              ? "Loading incentives..."
              : filteredList.length > 0
                ? `Showing ${filteredList.length} ${
                    activeTab === "all" ? "incentive" : activeTab
                  }${filteredList.length === 1 ? "" : "s"}`
                : `No ${emptyLabel} found`}
          </p>
        </div>

        {isInitialLoading ? (
          <div className="cms-agent-bonus-table-scroll">
            <table className="cms-agent-bonus-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Course</th>
                  <th>Intake</th>
                  <th>Nationality</th>
                  <th>Min Apps</th>
                  <th>Amount</th>
                  <th>Valid</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </tbody>
            </table>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="cms-agent-bonus-empty">
            <div className="cms-agent-bonus-empty-icon">
              <FaIcons.FaGift size={18} />
            </div>
            <p className="cms-agent-bonus-empty-title">
              No {emptyLabel} found
            </p>
            <p className="cms-agent-bonus-empty-sub">
              {permissions.can_edit_provider
                ? "Click Add Incentive to create one."
                : "There are no records for this filter yet."}
            </p>
          </div>
        ) : (
          <div className="cms-agent-bonus-table-scroll">
            <table className="cms-agent-bonus-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Course</th>
                  <th>Intake</th>
                  <th>Nationality</th>
                  <th>Min Apps</th>
                  <th>Amount</th>
                  <th>Valid</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((bonus) => (
                  <tr key={bonus.id}>
                    <td>
                      <span
                        className={`cms-agent-bonus-type-badge ${typeBadgeClass(
                          bonus.type,
                        )}`}
                      >
                        {bonus.type
                          ? bonus.type.charAt(0).toUpperCase() +
                            bonus.type.slice(1)
                          : "—"}
                      </span>
                    </td>
                    <td>
                      {renderBadgeList(
                        parseMultiValue(bonus.course_id).map((id) =>
                          getCourseName(id),
                        ),
                      )}
                    </td>
                    <td>
                      {renderBadgeList(
                        parseMultiValue(bonus.intake_id).map((id) =>
                          getIntakeTitle(id),
                        ),
                      )}
                    </td>
                    <td>
                      {renderBadgeList(
                        parseMultiValue(bonus.nationality).map((iso) =>
                          getCountryName(iso),
                        ),
                      )}
                    </td>
                    <td>
                      {bonus.min_applications === 0
                        ? "No min"
                        : `${bonus.min_applications}`}
                    </td>
                    <td>
                      <span className="cms-agent-bonus-amount">
                        {bonus.bonus_amount} {bonus.currency}
                      </span>
                    </td>
                    <td>
                      <span className="cms-agent-bonus-dates">
                        {new Date(bonus.valid_from).toLocaleDateString()} to{" "}
                        {new Date(bonus.valid_to).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      {bonus.is_active ? (
                        <span className="cms-agent-bonus-status-badge cms-agent-bonus-status-badge--active">
                          <span className="cms-agent-bonus-status-dot" />
                          Active
                        </span>
                      ) : (
                        <span className="cms-agent-bonus-status-badge cms-agent-bonus-status-badge--inactive">
                          <span className="cms-agent-bonus-status-dot" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="cms-agent-bonus-actions">
                        <button
                          type="button"
                          className="cms-agent-bonus-icon-btn cms-agent-bonus-icon-btn--edit"
                          onClick={() => handleEdit(bonus)}
                          title="Edit"
                        >
                          <FaIcons.FaEdit size={11} />
                        </button>
                        <button
                          type="button"
                          className="cms-agent-bonus-icon-btn cms-agent-bonus-icon-btn--delete"
                          onClick={() => handleDelete(bonus.id)}
                          title="Delete"
                        >
                          <FaIcons.FaTrashAlt size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        size="lg"
        backdrop={loading ? "static" : true}
        keyboard={!loading}
        backdropClassName="cms-agent-bonus-modal-backdrop"
        dialogClassName="cms-agent-bonus-modal-dialog"
        contentClassName="cms-agent-bonus-modal-content"
        className="cms-agent-bonus-modal"
      >
        <Modal.Header
          closeButton={!loading}
          className="cms-agent-bonus-modal-header"
        >
          <Modal.Title className="cms-agent-bonus-modal-title">
            {isEditMode ? "Edit Incentive" : "Add Incentive"}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSubmit} className="cms-agent-bonus-modal-form">
          <Modal.Body className="cms-agent-bonus-modal-body">
            <div className="cms-agent-bonus-field">
              <label className="cms-agent-bonus-field-label">
                Incentive Type
              </label>
              <div className="cms-agent-bonus-type-options">
                {["bonus", "offer", "scholarship"].map((type) => (
                  <div className="cms-agent-bonus-type-option" key={type}>
                    <input
                      type="radio"
                      name="type"
                      id={`type${type.charAt(0).toUpperCase() + type.slice(1)}`}
                      value={type}
                      checked={formData.type === type}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor={`type${
                        type.charAt(0).toUpperCase() + type.slice(1)
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="cms-agent-bonus-field">
              <label className="cms-agent-bonus-field-label">Courses</label>
              <Select
                isMulti={!isEditMode}
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={courseOptions}
                placeholder="Select one or more courses..."
                value={selectedCourseValue}
                onChange={(selected) => {
                  if (isEditMode) {
                    const value = selected ? selected.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      course_id: value ? [value] : [],
                    }));
                  } else {
                    const values = Array.isArray(selected)
                      ? selected.map((s) => s.value)
                      : [];
                    setFormData((prev) => ({
                      ...prev,
                      course_id: values,
                    }));
                  }
                }}
              />
            </div>

            <div className="cms-agent-bonus-field">
              <label className="cms-agent-bonus-field-label">Intakes</label>
              <Select
                isMulti={!isEditMode}
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={intakeOptions}
                placeholder="Select one or more intakes..."
                value={selectedIntakeValue}
                onChange={(selected) => {
                  if (isEditMode) {
                    const value = selected ? selected.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      intake_id: value ? [value] : [],
                    }));
                  } else {
                    const values = Array.isArray(selected)
                      ? selected.map((s) => s.value)
                      : [];
                    setFormData((prev) => ({
                      ...prev,
                      intake_id: values,
                    }));
                  }
                }}
              />
              <span className="cms-agent-bonus-field-hint">
                Showing {allIntakes.length} intakes from all courses.
              </span>
            </div>

            <div className="cms-agent-bonus-field">
              <label className="cms-agent-bonus-field-label">
                Nationalities
              </label>
              <Select
                isMulti={!isEditMode}
                isClearable
                closeMenuOnSelect={false}
                menuPortalTarget={document.body}
                styles={selectStyles}
                options={nationalityOptions}
                placeholder="Select one or more nationalities..."
                value={selectedNationalityValue}
                onChange={(selected) => {
                  if (isEditMode) {
                    const value = selected ? selected.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      nationality: value ? [value] : [],
                    }));
                  } else {
                    const values = Array.isArray(selected)
                      ? selected.map((s) => s.value)
                      : [];
                    setFormData((prev) => ({
                      ...prev,
                      nationality: values,
                    }));
                  }
                }}
              />
            </div>

            <div className="cms-agent-bonus-field">
              <div className="cms-agent-bonus-min-apps-header">
                <label className="cms-agent-bonus-field-label" style={{ marginBottom: 0 }}>
                  Minimum Applications
                </label>
                <label className="cms-agent-bonus-check" htmlFor="noMinApps">
                  <input
                    type="checkbox"
                    id="noMinApps"
                    checked={formData.min_applications === 0}
                    onChange={toggleMinApps}
                  />
                  No minimum
                </label>
              </div>
              {formData.min_applications > 0 && (
                <input
                  type="number"
                  className="cms-agent-bonus-input"
                  min="1"
                  value={formData.min_applications}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      min_applications: parseInt(e.target.value) || 1,
                    }))
                  }
                />
              )}
              <span className="cms-agent-bonus-field-hint">
                {formData.min_applications === 0
                  ? "Applies unconditionally."
                  : `Applies only when ≥ ${formData.min_applications} apps are submitted.`}
              </span>
            </div>

            <div className="cms-agent-bonus-field-row cms-agent-bonus-field-row--amount">
              <div className="cms-agent-bonus-field">
                <label className="cms-agent-bonus-field-label">
                  Amount <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="cms-agent-bonus-input"
                  name="bonus_amount"
                  placeholder="0.00"
                  value={formData.bonus_amount ?? ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="cms-agent-bonus-field">
                <label className="cms-agent-bonus-field-label">Currency</label>
                <select
                  className="cms-agent-bonus-select"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="AUD">AUD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div className="cms-agent-bonus-field-row">
              <div className="cms-agent-bonus-field">
                <label className="cms-agent-bonus-field-label">
                  Valid From <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className="cms-agent-bonus-input"
                  name="valid_from"
                  value={formData.valid_from ?? ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="cms-agent-bonus-field">
                <label className="cms-agent-bonus-field-label">
                  Valid To <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className="cms-agent-bonus-input"
                  name="valid_to"
                  value={formData.valid_to ?? ""}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="cms-agent-bonus-field">
              <label className="cms-agent-bonus-field-label">Status</label>
              <label className="cms-agent-bonus-switch" htmlFor="isActiveSwitch">
                <input
                  type="checkbox"
                  id="isActiveSwitch"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                {formData.is_active ? "Active" : "Inactive"}
              </label>
            </div>

            <div className="cms-agent-bonus-field" style={{ marginBottom: 0 }}>
              <label className="cms-agent-bonus-field-label">
                Description (Optional)
              </label>
              <textarea
                className="cms-agent-bonus-textarea"
                name="description"
                rows="3"
                value={formData.description ?? ""}
                onChange={handleInputChange}
                placeholder="Optional notes about this incentive..."
              />
            </div>
          </Modal.Body>

          <Modal.Footer className="cms-agent-bonus-modal-footer">
            <button
              type="button"
              className="cms-agent-bonus-btn-secondary"
              onClick={closeModal}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cms-agent-bonus-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaIcons.FaSpinner size={12} className="cms-agent-bonus-spinner" />
                  Saving...
                </>
              ) : isEditMode ? (
                "Update Incentive"
              ) : (
                "Save Incentive"
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default AgentBonus;
